import {
  PERSONALITY_TRAITS,
  archetypeFor,
  type PersonalityTrait,
  type TraitPole,
  type PersonalityArchetype,
} from '../data/personalityArchetypes';

/**
 * Reverse-score a Likert response (spec §4): a "strongly agree" (5) on a reverse
 * item should count as a "strongly disagree" (1). For a 5-point scale this is
 * 6 − raw. Used by assessmentStore.calculateResults (guarded by scoring_key.reverse).
 */
export function reverseLikert(raw: number, points = 5): number {
  return points + 1 - raw;
}

/**
 * Convert a trait's averaged 1–5 Likert score into a 0–100% spectrum position.
 * 1 → 0%, 3 → 50%, 5 → 100%. Clamped.
 */
export function traitPercent(avg5: number): number {
  const pct = ((avg5 - 1) / 4) * 100;
  return Math.max(0, Math.min(100, Math.round(pct)));
}

export type TraitBand = 'low' | 'balanced' | 'high';

/** Spectrum band for a trait % (spec §4): <40 Low · 40–60 Balanced · >60 High. */
export function traitBand(pct: number): TraitBand {
  if (pct < 40) return 'low';
  if (pct > 60) return 'high';
  return 'balanced';
}

export interface TraitRead {
  trait: PersonalityTrait;
  pct: number;
  /** Which pole the score leans to (>=50 high, else low). */
  pole: TraitPole;
  /** Distance from the 50% midpoint — drives archetype selection. */
  deviation: number;
  band: TraitBand;
}

export interface PersonalityProfile {
  /** Trait reads in canonical order (for the spectrum bars). */
  reads: TraitRead[];
  /** Largest-deviation trait → primary archetype. */
  primary: PersonalityArchetype;
  /** Second-largest-deviation trait → modifier/streak archetype. */
  modifier: PersonalityArchetype;
  /** "The Catalyst, with a Steward streak". */
  headline: string;
}

/**
 * Derive the Metanoia persona from the 5 trait percentages (0–100). The trait
 * furthest from the 50% midpoint becomes the primary archetype (on its leaning
 * pole); the next-furthest becomes the modifier/streak. Ties fall back to the
 * canonical trait order. (spec §6)
 */
export function derivePersonality(percents: Record<string, number>): PersonalityProfile {
  const reads: TraitRead[] = PERSONALITY_TRAITS.map(t => {
    const pct = Math.max(0, Math.min(100, Math.round(percents[t.key] ?? 50)));
    const pole: TraitPole = pct >= 50 ? 'high' : 'low';
    return { trait: t.key, pct, pole, deviation: Math.abs(pct - 50), band: traitBand(pct) };
  });

  // Largest deviation first; stable canonical order breaks ties.
  const byDeviation = reads
    .map((r, i) => ({ r, i }))
    .sort((a, b) => b.r.deviation - a.r.deviation || a.i - b.i)
    .map(x => x.r);

  const primaryRead = byDeviation[0];
  const modifierRead = byDeviation[1];
  const primary = archetypeFor(primaryRead.trait, primaryRead.pole);
  const modifier = archetypeFor(modifierRead.trait, modifierRead.pole);

  return {
    reads,
    primary,
    modifier,
    headline: `${primary.name}, with a ${modifier.name.replace(/^The\s+/, '')} streak`,
  };
}

/** Convenience: derive straight from the stored dimension averages (trait → 1–5). */
export function derivePersonalityFromAverages(avgByTrait: Record<string, number>): PersonalityProfile {
  const percents: Record<string, number> = {};
  for (const t of PERSONALITY_TRAITS) percents[t.key] = traitPercent(avgByTrait[t.key] ?? 3);
  return derivePersonality(percents);
}
